import {
  useState,
  useCallback,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { toast } from "@/lib/toast";
import { Card } from "@sphinx/design-system/card";
import { Button } from "@sphinx/design-system/button";
import { Separator } from "@sphinx/design-system/separator";
import { Switch } from "@sphinx/design-system/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@sphinx/design-system/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@sphinx/design-system/tooltip";
import {
  MoreVertical,
  Link2Off,
  RefreshCw,
  Power,
  PowerOff,
  Loader2,
  Copy,
  Download,
  Check,
  Edit,
  AlertCircle,
  FileText,
  FolderInput,
} from "lucide-react";
import { ServerWithName } from "@/hooks/use-app-state";
import { exportServerApi } from "@/lib/apis/mcp-export-api";
import { ErrorCard } from "@/components/ui/error-card";
import {
  getConnectionStatusMeta,
  getServerCommandDisplay,
  getServerUrl,
} from "./server-card-utils";
import { track } from "@/lib/analytics";
import { useAppNavigate } from "@/lib/app-navigation";
import { usePreviewedHostId } from "@/hooks/use-previewed-client-id";
import { isProtocolVersionPinFailure } from "@/lib/protocol-version-pin";
import { buildHostFocusTabPath } from "@/components/hosts/host-verify-deep-link";
import type { ServerDetailTab } from "./ServerDetailModal";
import { downloadJsonFile } from "@/lib/json-config-parser";
import { generateAgentBrief } from "@/lib/generate-agent-brief";
import { HOSTED_MODE } from "@/lib/config";
import { useExploreCasesPrefetchOnConnect } from "@/hooks/use-explore-cases-prefetch-on-connect";
import { getOAuthTraceFailureStep } from "@/lib/oauth/oauth-trace";
import { SEEDED_CONNECTOR_NAMES } from "@/shared/sphinx";

function isHostedInsecureHttpServer(server: ServerWithName): boolean {
  if (!HOSTED_MODE || !("url" in server.config) || !server.config.url) {
    return false;
  }

  try {
    return new URL(server.config.url.toString()).protocol === "http:";
  } catch {
    return false;
  }
}

const SERVER_CARD_CONTEXT_MENU_EXEMPT_SELECTOR =
  "[data-server-card-context-menu-exempt]";

function isContextMenuExemptTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest(SERVER_CARD_CONTEXT_MENU_EXEMPT_SELECTOR) != null
  );
}

interface ServerConnectionCardProps {
  server: ServerWithName;
  needsReconnect?: boolean;
  onDisconnect: (serverName: string) => void;
  onReconnect: (
    serverName: string,
    options?: {
      forceOAuthFlow?: boolean;
      allowInteractiveOAuthFlow?: boolean;
    }
  ) => Promise<void>;
  onRemove?: (serverName: string) => void;
  hostedServerId?: string;
  onOpenDetailModal?: (
    server: ServerWithName,
    defaultTab: ServerDetailTab
  ) => void;
  /** When set (e.g. active project on Servers tab), prefetches Explore AI test cases on MCP connect. */
  projectId?: string | null;
  /**
   * Projects this server can be moved into via the actions menu. Already
   * excludes the current project. When omitted/empty the "Move to project"
   * item is hidden.
   */
  moveTargets?: Array<{ id: string; name: string; icon?: string }>;
  /** Moves this server into another project (create in target, remove here). */
  onMoveToProject?: (
    serverName: string,
    targetProjectId: string
  ) => void | Promise<void>;
  /** True while a move for this server is in flight. */
  isMovingToProject?: boolean;
  /**
   * The persisted `config.enabled === false` state — the connector is switched
   * off, so nothing connects it and its last connect failure is no longer
   * news. Distinct from Remove: the row survives, and seeded connectors (which
   * cannot be removed) can be disabled.
   */
  isDisabled?: boolean;
  /** Persists the enable flag. Omitted → the disable/enable item is hidden. */
  onSetEnabled?: (serverName: string, enabled: boolean) => void | Promise<void>;
  /**
   * The client this card's page is scoped to, and whether this connector is in
   * that client's `targets.server_ids`. Distinct from `isDisabled`, which is
   * the connector's own on/off for every client: this is "does THIS client
   * attach it in a run". Both omitted → no attach control (drag overlay).
   */
  attachedToClientName?: string;
  isAttached?: boolean;
  /** Attach this connector to EVERY client's runs (all targets' server_ids). */
  onAttachToAllClients?: (serverName: string) => void | Promise<void>;
  onSetAttached?: (
    serverName: string,
    attached: boolean
  ) => void | Promise<void>;
}

export function ServerConnectionCard({
  server,
  needsReconnect = false,
  onDisconnect,
  onReconnect,
  onRemove,
  hostedServerId,
  onOpenDetailModal,
  projectId,
  moveTargets,
  onMoveToProject,
  isMovingToProject = false,
  isDisabled = false,
  onSetEnabled,
  attachedToClientName,
  isAttached = false,
  onAttachToAllClients,
  onSetAttached,
}: ServerConnectionCardProps) {
  useExploreCasesPrefetchOnConnect(projectId ?? null, server, hostedServerId);

  // A pinned protocol version the server doesn't offer is the one connect
  // failure with an exact, one-click fix, so the card offers it instead of
  // leaving the user to find the dropdown. `usePreviewedHostId` is the shared
  // subscription every host-aware surface reads — the pin comes from whichever
  // client is being previewed, so that is the client to open.
  const navigate = useAppNavigate();
  const [previewedHostId] = usePreviewedHostId(projectId ?? null);
  const isProtocolPinFailure = isProtocolVersionPinFailure(
    server.lastNormalizedError,
    server.lastError
  );
  const protocolPinAction = isProtocolPinFailure
    ? {
        label: "Change protocol version",
        onClick: () => {
          track("change_protocol_version_clicked", {
            location: "server_connection_card",
            has_host_id: Boolean(previewedHostId),
          });
          navigate(buildHostFocusTabPath(previewedHostId, "protocol"));
        },
      }
    : undefined;

  const [isReconnecting, setIsReconnecting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isCopyingBrief, setIsCopyingBrief] = useState(false);
  const [isErrorExpanded, setIsErrorExpanded] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);

  const {
    label: connectionStatusLabel,
    indicatorColor,
    Icon: ConnectionStatusIcon,
    iconClassName,
  } = getConnectionStatusMeta(server.connectionStatus);
  const commandDisplay = getServerCommandDisplay(server.config);

  const initializationInfo = server.initializationInfo;

  // Extract server info from initializationInfo
  const serverIcon = initializationInfo?.serverVersion?.icons?.[0];
  const version = initializationInfo?.serverVersion?.version;

  // A disabled connector's last failure is history, not a live problem: the
  // card reports "Disabled" and drops the error badge and the error card, so
  // switching a broken connector off actually makes it stop shouting.
  const hasError =
    !isDisabled &&
    server.connectionStatus === "failed" &&
    Boolean(server.lastError);
  const oauthFailureStep = getOAuthTraceFailureStep(server.lastOAuthTrace);
  const isHostedHttpReconnectBlocked = isHostedInsecureHttpServer(server);
  const isPendingConnection =
    server.connectionStatus === "connecting" ||
    server.connectionStatus === "oauth-flow";
  const isReconnectMenuDisabled = isReconnecting || isPendingConnection;
  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000); // Reset after 2 seconds
    } catch (error) {
      console.error("Failed to copy text:", error);
    }
  };

  const handleReconnect = async (options?: {
    forceOAuthFlow?: boolean;
    allowInteractiveOAuthFlow?: boolean;
  }) => {
    setIsReconnecting(true);
    try {
      await onReconnect(server.name, options);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      toast.error(`Failed to reconnect to ${server.name}: ${errorMessage}`);
    } finally {
      setIsReconnecting(false);
    }
  };

  const getSwitchReconnectOptions = () => {
    if (server.useOAuth === true && !server.oauthTokens) {
      return { allowInteractiveOAuthFlow: true };
    }

    return { allowInteractiveOAuthFlow: false };
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const toastId = toast.loading(`Exporting ${server.name}…`);
      const data = await exportServerApi(server.name);
      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      const filename = `mcp-server-export_${server.name}_${ts}.json`;
      downloadJsonFile(filename, data);
      toast.success(`Exported ${server.name} info to ${filename}`, {
        id: toastId,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      toast.error(`Failed to export ${server.name}: ${errorMessage}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleCopyAgentBrief = async () => {
    setIsCopyingBrief(true);
    try {
      const data = await exportServerApi(server.name);
      const serverUrl = getServerUrl(server.config);
      const markdown = generateAgentBrief(data, { serverUrl });
      await navigator.clipboard.writeText(markdown);
      toast.success("Agent brief copied to clipboard");
      track("copy_agent_brief_clicked", {
        location: "server_connection_card",
        server_id: server.name,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      toast.error(`Failed to copy agent brief: ${errorMessage}`);
    } finally {
      setIsCopyingBrief(false);
    }
  };

  const isDetailModalEnabled = onOpenDetailModal != null;

  const openDetailModal = useCallback(
    (tab: ServerDetailTab, source: "card_click" | "kebab_edit") => {
      if (!onOpenDetailModal) {
        return;
      }
      onOpenDetailModal(server, tab);
      track("server_detail_modal_opened", {
        location: "server_connection_card",
        source,
        default_tab: tab,
        server_id: server.name,
      });
    },
    [onOpenDetailModal, server]
  );

  const handleCardContextMenu = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (isContextMenuExemptTarget(event.target)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setIsActionsMenuOpen(true);
    },
    []
  );

  const handleCardClick = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (!isDetailModalEnabled) {
        return;
      }

      const shouldSuppressCardClick = event.ctrlKey || isActionsMenuOpen;
      if (shouldSuppressCardClick) {
        return;
      }

      track("server_card_clicked", {
        location: "server_connection_card",
        server_id: server.name,
      });
      openDetailModal("configuration", "card_click");
    },
    [isActionsMenuOpen, isDetailModalEnabled, server.name, openDetailModal]
  );

  return (
    <>
      <Card
        data-connector-disabled={isDisabled ? "true" : undefined}
        className={`group h-full rounded-xl border border-border/50 bg-card/60 p-0 shadow-sm transition-all duration-200 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none ${
          isDetailModalEnabled
            ? "cursor-pointer hover:border-border hover:shadow-md hover:border-primary/40"
            : ""
        } ${isDisabled ? "opacity-55 saturate-50" : ""}`}
        onContextMenu={handleCardContextMenu}
        onClick={isDetailModalEnabled ? handleCardClick : undefined}
      >
        <div className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {serverIcon?.src && (
                  <img
                    src={serverIcon.src}
                    alt={`${server.name} icon`}
                    className="h-5 w-5 flex-shrink-0 rounded"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                )}
                <h3 className="truncate text-sm font-semibold text-foreground">
                  {server.name}
                </h3>
                {version && (
                  <span className="text-xs text-muted-foreground">
                    v{version}
                  </span>
                )}
              </div>

              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {onSetAttached && attachedToClientName ? (
                  <button
                    type="button"
                    data-server-card-context-menu-exempt
                    data-testid={`connector-attached-${server.name}`}
                    aria-pressed={isAttached}
                    aria-label={`Use ${server.name} in ${attachedToClientName} runs`}
                    title={`Whether ${attachedToClientName} runs are wired up with this connector`}
                    onClick={(e) => {
                      e.stopPropagation();
                      void onSetAttached(server.name, !isAttached);
                    }}
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
                      isAttached
                        ? "border-primary/50 bg-primary/10 text-foreground"
                        : "border-border/60 text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <span
                      className={`flex size-3 items-center justify-center rounded-[3px] border ${
                        isAttached
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border"
                      }`}
                    >
                      {isAttached ? (
                        <Check className="size-2" strokeWidth={4} />
                      ) : null}
                    </span>
                    {isAttached
                      ? `Used in ${attachedToClientName} runs`
                      : `Not used in ${attachedToClientName} runs`}
                  </button>
                ) : null}
                {onAttachToAllClients ? (
                  <button
                    type="button"
                    data-server-card-context-menu-exempt
                    data-testid={`connector-attach-all-${server.name}`}
                    aria-label={`Use ${server.name} in every client's runs`}
                    title="Wire this connector into every client's runs"
                    onClick={(e) => {
                      e.stopPropagation();
                      void onAttachToAllClients(server.name);
                    }}
                    className="inline-flex items-center gap-1 rounded-full border border-border/60 px-2 py-0.5 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
                  >
                    Use in all clients
                  </button>
                ) : null}
                {hasError && (
                  <button
                    data-server-card-context-menu-exempt
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsErrorExpanded(true);
                    }}
                    className="inline-flex items-center gap-1 rounded-full border border-red-300/60 bg-red-500/10 px-2 py-0.5 text-[11px] text-red-700 dark:text-red-300 cursor-pointer"
                  >
                    <AlertCircle className="h-3 w-3" />
                    Error
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-col items-end gap-1.5">
              <div
                className="flex items-center gap-1.5"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="inline-flex items-center gap-1.5 px-1 text-[11px] text-muted-foreground">
                  {isDisabled ? (
                    <>
                      <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50" />
                      <span>Disabled</span>
                    </>
                  ) : (
                    <>
                      {isPendingConnection ? (
                        <ConnectionStatusIcon className={iconClassName} />
                      ) : (
                        <span
                          className="h-1.5 w-1.5 rounded-full"
                          style={{ backgroundColor: indicatorColor }}
                        />
                      )}
                      <span>
                        {server.connectionStatus === "failed"
                          ? `${connectionStatusLabel} (${server.retryCount})`
                          : connectionStatusLabel}
                      </span>
                    </>
                  )}
                  {needsReconnect && !isDisabled ? (
                    <Tooltip>
                      <TooltipTrigger
                        type="button"
                        aria-label="Connection settings changed"
                        className="inline-flex h-4 w-4 items-center justify-center rounded-full text-amber-600 outline-none transition-colors hover:text-amber-700 focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background dark:text-amber-300 dark:hover:text-amber-200"
                      >
                        <Power className="h-3 w-3" />
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        sideOffset={4}
                        variant="muted"
                        className="max-w-48 px-2.5 text-left [text-wrap:normal]"
                      >
                        Turn the connection off and on to apply the new
                        connection settings.
                      </TooltipContent>
                    </Tooltip>
                  ) : null}
                </span>

                <Switch
                  data-server-card-context-menu-exempt
                  // The switch is live connect/disconnect, not the persisted
                  // flag; a disabled connector has nothing to connect, so it
                  // sits off and inert until it is enabled again.
                  disabled={isDisabled}
                  checked={
                    !isDisabled && server.connectionStatus === "connected"
                  }
                  onCheckedChange={(checked) => {
                    track("connection_switch_toggled", {
                      location: "server_connection_card",
                    });
                    if (checked && isHostedHttpReconnectBlocked) {
                      toast.error(
                        "HTTP servers are not supported in hosted mode"
                      );
                      return;
                    }
                    if (!checked) {
                      onDisconnect(server.name);
                    } else {
                      void handleReconnect(getSwitchReconnectOptions());
                    }
                  }}
                  className="cursor-pointer scale-75"
                />

                <DropdownMenu
                  open={isActionsMenuOpen}
                  onOpenChange={setIsActionsMenuOpen}
                >
                  <DropdownMenuTrigger asChild>
                    <Button
                      aria-label={`Open actions menu for ${server.name}`}
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-muted-foreground/70 hover:text-foreground cursor-pointer"
                    >
                      <MoreVertical className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    {/* A disabled connector has nothing to reconnect to —
                        enable it first. */}
                    {isDisabled ? null : (
                      <DropdownMenuItem
                        onClick={() => {
                          if (isHostedHttpReconnectBlocked) {
                            toast.error(
                              "HTTP servers are not supported in hosted mode"
                            );
                            return;
                          }
                          track("reconnect_server_clicked", {
                            location: "server_connection_card",
                          });
                          // A tokenless Auto server carries `useOAuth: true`
                          // as a derived mirror even when it connected
                          // unauthenticated, so that flag alone can't force
                          // the flow — it would redirect an open server into
                          // an OAuth it has no authorization server for.
                          // Mirror the orchestrator's Auto guard and let the
                          // normal reconnect escalate on a real 401 instead.
                          const isTokenlessAutoServer =
                            server.authMethod === "auto" &&
                            server.oauthTokens == null;
                          const shouldForceOAuth =
                            !isTokenlessAutoServer &&
                            (server.useOAuth === true ||
                              server.oauthTokens != null);
                          void handleReconnect(
                            shouldForceOAuth
                              ? { forceOAuthFlow: true }
                              : undefined
                          );
                        }}
                        disabled={isReconnectMenuDisabled}
                        className="text-xs cursor-pointer"
                      >
                        {isReconnecting ? (
                          <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3 w-3 mr-2" />
                        )}
                        {isReconnecting ? "Reconnecting..." : "Reconnect"}
                      </DropdownMenuItem>
                    )}
                    {onSetEnabled ? (
                      <DropdownMenuItem
                        onClick={() => {
                          track(
                            isDisabled
                              ? "enable_server_clicked"
                              : "disable_server_clicked",
                            { location: "server_connection_card" }
                          );
                          // Disabling drops the live connection too:
                          // persisting the flag alone would leave a connector
                          // that is off on the next load but still in the
                          // playground's active set right now.
                          if (!isDisabled) {
                            onDisconnect(server.name);
                          }
                          void onSetEnabled(server.name, isDisabled);
                        }}
                        className="text-xs cursor-pointer"
                      >
                        {isDisabled ? (
                          <Power className="h-3 w-3 mr-2" />
                        ) : (
                          <PowerOff className="h-3 w-3 mr-2" />
                        )}
                        {isDisabled ? "Enable connector" : "Disable connector"}
                      </DropdownMenuItem>
                    ) : null}
                    <DropdownMenuItem
                      onClick={() => {
                        track("edit_server_clicked", {
                          location: "server_connection_card",
                        });
                        openDetailModal("configuration", "kebab_edit");
                      }}
                      className="text-xs cursor-pointer"
                    >
                      <Edit className="h-3 w-3 mr-2" />
                      Configure
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        track("export_server_clicked", {
                          location: "server_connection_card",
                        });
                        handleExport();
                      }}
                      disabled={
                        isExporting || server.connectionStatus !== "connected"
                      }
                      className="text-xs cursor-pointer"
                    >
                      {isExporting ? (
                        <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                      ) : (
                        <Download className="h-3 w-3 mr-2" />
                      )}
                      {isExporting ? "Exporting..." : "Export server info"}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        handleCopyAgentBrief();
                      }}
                      disabled={
                        isCopyingBrief ||
                        server.connectionStatus !== "connected"
                      }
                      className="text-xs cursor-pointer"
                    >
                      {isCopyingBrief ? (
                        <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                      ) : (
                        <FileText className="h-3 w-3 mr-2" />
                      )}
                      {isCopyingBrief
                        ? "Copying..."
                        : "Copy markdown for evals"}
                    </DropdownMenuItem>
                    {onMoveToProject &&
                    moveTargets &&
                    moveTargets.length > 0 ? (
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger
                          disabled={isMovingToProject}
                          className="gap-2 text-xs cursor-pointer [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground"
                        >
                          {isMovingToProject ? (
                            <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                          ) : (
                            <FolderInput className="h-3 w-3 mr-2" />
                          )}
                          {isMovingToProject ? "Moving..." : "Move to project"}
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent
                          sideOffset={4}
                          className="max-h-64 w-52 overflow-y-auto"
                        >
                          {moveTargets.map((target) => (
                            <DropdownMenuItem
                              key={target.id}
                              className="text-xs cursor-pointer"
                              onClick={() => {
                                track("move_server_to_project_clicked", {
                                  location: "server_connection_card",
                                });
                                void onMoveToProject(server.name, target.id);
                              }}
                            >
                              <span className="flex size-4 shrink-0 items-center justify-center rounded bg-primary/10 text-[9px] font-semibold text-primary">
                                {target.name.charAt(0).toUpperCase()}
                              </span>
                              <span className="truncate">{target.name}</span>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                    ) : null}
                    {/* Seeded connectors are the fixed matrix; the server
                        refuses their DELETE too (409). */}
                    {SEEDED_CONNECTOR_NAMES.has(server.name) ? null : (
                      <>
                        <Separator />
                        <DropdownMenuItem
                          className="text-destructive text-xs cursor-pointer"
                          onClick={() => {
                            track("remove_server_clicked", {
                              location: "server_connection_card",
                            });
                            onDisconnect(server.name);
                            onRemove?.(server.name);
                          }}
                        >
                          <Link2Off className="h-3 w-3 mr-2" />
                          Remove server
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>

          <div className="relative mt-2 rounded-md border border-border/50 bg-muted/30 p-2 pr-8 font-mono text-xs text-muted-foreground">
            <div className="break-all">{commandDisplay}</div>
            <button
              aria-label="Copy server command"
              data-server-card-context-menu-exempt
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(commandDisplay, "command");
              }}
              className="absolute right-1 top-1 p-1 text-muted-foreground/60 transition-colors hover:text-foreground cursor-pointer"
            >
              {copiedField === "command" ? (
                <Check className="h-3 w-3" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </button>
          </div>

          {hasError && (
            <div className="mt-3" onClick={(e) => e.stopPropagation()}>
              {oauthFailureStep ? (
                <div className="mb-1 text-xs font-medium text-red-700 dark:text-red-300">
                  OAuth failed during {oauthFailureStep.title}
                </div>
              ) : null}
              <ErrorCard
                // Prefer the rich block; fall back to the message string
                // (the card calls `describeError` internally when needed).
                error={server.lastNormalizedError ?? server.lastError ?? ""}
                // Controlled — the Error badge above toggles
                // `isErrorExpanded`; the card must reflect that on every
                // change, not just at mount.
                open={isErrorExpanded}
                onOpenChange={setIsErrorExpanded}
                action={protocolPinAction}
              />
              {server.retryCount > 0 && (
                <div className="mt-1 text-xs text-muted-foreground">
                  {server.retryCount} retry attempt
                  {server.retryCount !== 1 ? "s" : ""}
                </div>
              )}
            </div>
          )}
        </div>
      </Card>
    </>
  );
}
